import React, { useEffect, useRef, useState } from 'react'
import { SenderCard } from './sendercard';
import { UserCard } from './usercard';
import { motion } from 'framer-motion';
import { messageVariants } from '@/App/pages/Messages';
import { useGetMessagesQuery, useSendMessageMutation } from '@/App/services/messagesSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import Loader, { FormLoader } from '@/Components/loader';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from '@/App/services/authSlice';
import ChatHeader from './chatheader';
import empty from "../../Images/noWorksheet.png";
import Pusher from 'pusher-js';
import * as Icon from 'react-feather'
import { handleError } from '@/App/utils/renderErrors';

const ChatBox = ({ currentChat, user, setShowNewChatSearch, refetchConvos, detailsLoading }) => {

    const token = useSelector(selectCurrentToken);
    const messagesEndRef = useRef();
    const pusherRef = useRef(null);
    const channelRef = useRef(null);
    // Refs to avoid stale closures inside Pusher event handlers
    const refetchRef = useRef(null);
    const refetchConvosRef = useRef(refetchConvos);

    const [text, setText] = useState('');
    const [messages, setMessages] = useState([]);
    const conversationId = currentChat?.id;

    const { data: chatMessages, isLoading, refetch, isUninitialized } = useGetMessagesQuery(
        conversationId ?? skipToken,
        { refetchOnFocus: false }
    );
    const [sendMessage] = useSendMessageMutation();

    // Keep refs current so Pusher handlers never use stale callbacks
    useEffect(() => { refetchRef.current = refetch; }, [refetch]);
    useEffect(() => { refetchConvosRef.current = refetchConvos; }, [refetchConvos]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView?.({ behavior: "instant", block: 'end', inline: 'nearest' });
    };

    // Pusher: create a fresh connection per (token, conversationId) pair.
    // Keyed on currentChat?.id (not the full object) so switching to the same
    // conversation via a new object reference does NOT re-subscribe and cause
    // duplicate event handlers.
    useEffect(() => {
        if (!token || !currentChat?.id) return;

        const channelName = 'private-conversation.' + currentChat.id;

        const pusher = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
            cluster: 'mt1',
            encrypted: true,
            authEndpoint: `${import.meta.env.VITE_BASE_API_URL}/broadcasting/auth`,
            auth: {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        });

        pusherRef.current = pusher;

        const pusherChannel = pusher.subscribe(channelName);
        channelRef.current = pusherChannel;

        // Once the subscription is confirmed, refetch messages to catch any
        // events that were broadcast during the connection window.
        pusherChannel.bind('pusher:subscription_succeeded', () => {
            refetchRef.current?.();
        });

        pusherChannel.bind('new-message', (data) => {
            const incoming = data?.data;
            setMessages((prev) => {
                if (incoming?.id && prev.some((m) => m.id === incoming.id)) return prev;
                return [...prev, { ...incoming }];
            });
            scrollToBottom();
            refetchConvosRef.current?.();
            // Re-sync canonical message payload (including server-side formatted timestamps)
            // so live-received messages show the same time as after a manual refresh.
            refetchRef.current?.();
        });

        return () => {
            pusherChannel.unbind_all();
            pusher.unsubscribe(channelName);
            pusher.disconnect();
            pusherRef.current = null;
            channelRef.current = null;
        };
    }, [token, currentChat?.id]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const isYesterday = date.toDateString() === yesterday.toDateString();
        if (isToday) return "Today";
        if (isYesterday) return "Yesterday";
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    const addDateIndicators = (messages) => {
        let newMessages = [];
        let currentDate = null;
        for (let i = 0; i < messages.length; i++) {
            const message = messages[i];
            const messageDate = new Date(message.created_at).toDateString();
            const formattedDate = formatDate(message.created_at);
            if (currentDate !== messageDate) {
                newMessages.push({
                    id: `date-${messageDate}`,
                    message_type: 'date',
                    date: formattedDate,
                });
                currentDate = messageDate;
            }
            newMessages.push(message);
        }
        return newMessages;
    };

    const updatedMessages = addDateIndicators(messages);

    const renderMessages = () => {
        return updatedMessages?.map((message, index) => {
            if (message?.sender_id === user?.id) {
                return (
                    <UserCard
                        key={index}
                        text={message?.message}
                        time={message?.created_at}
                    />
                );
            } else if (message?.sender_id && message?.sender_id !== user?.id) {
                return (
                    <SenderCard
                        key={index}
                        text={message?.message}
                        time={message?.created_at}
                        senderImage={currentChat?.user_id?.avatar}
                        senderName={currentChat?.user_id?.name}
                    />
                );
            } else if (message?.message_type === 'date') {
                return (
                    <div key={`date-${index}`} className="flex gap-3 items-center justify-center w-full z-40 py-3">
                        <span className='text-black bg-[#0D4044] bg-opacity-15 p-1 px-2 rounded-xl text-[10px]'>{message?.date}</span>
                    </div>
                );
            }
            return null;
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!text || text === '') return;

        const receiverId = currentChat?.user_id?.id;
        if (!receiverId) {
            toast.error("Cannot send message: recipient not found");
            return;
        }

        // Save text before clearing so we can restore it on failure
        const savedText = text;
        setText('');

        try {
            const res = await sendMessage({
                message: savedText,
                receiver_id: String(receiverId),
            }).unwrap();

            if (res?.data) {
                setMessages((prev) => {
                    if (res.data?.id && prev.some((m) => m.id === res.data.id)) return prev;
                    return [...prev, res.data];
                });
                scrollToBottom();
            }
            if (messages.length === 0 || messages.length === 1) {
                refetchConvos();
            }
        } catch (error) {
            setText(savedText); // restore text so the therapist doesn't lose their message
            const errorMessage = handleError(error);
            toast.error(errorMessage);
        }
    };

    useEffect(() => {
        messages && scrollToBottom();
    }, [messages]);

    useEffect(() => {
        chatMessages && scrollToBottom();
    }, [chatMessages]);

    // Reset + refetch when the active conversation changes.
    // Keyed on conversationId (primitive) so a new currentChat object with
    // the same id does NOT trigger a redundant refetch + state reset.
    useEffect(() => {
        setMessages([]);
        !isUninitialized && refetch();
    }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Merge server data with local state whenever the query result updates.
    // Previously this replaced all local messages, which wiped any messages
    // added optimistically (via handleSubmit or Pusher) during a refetch window.
    useEffect(() => {
        if (!currentChat || !chatMessages) return;
        const serverMessages = [...(chatMessages.data ?? [])].reverse();
        setMessages((prev) => {
            const serverIds = new Set(serverMessages.map((m) => m.id));
            const localOnly = prev.filter((m) => m.id && !serverIds.has(m.id));
            return [...serverMessages, ...localOnly];
        });
        scrollToBottom();
    }, [chatMessages]); // eslint-disable-line react-hooks/exhaustive-deps


    return (
        <div className='h-full flex flex-col w-full'>
            {
                currentChat ?
                    <>
                        <ChatHeader
                            currentChat={currentChat}
                        />
                        <div className="w-full h-full flex-col p-6 overflow-y-auto flex-grow">
                            {
                                isLoading ?
                                    <div className='flex items-center justify-center flex-col m-auto w-full h-full p-6 overflow-y-auto'>
                                        <div>
                                            <FormLoader />
                                        </div>
                                        <p className="mt-3 text-sm text-[#868C98] text-center">
                                            Loading Messages...
                                        </p>
                                    </div>
                                    :
                                    messages?.length === 0 ?
                                        <div className='flex items-center justify-center m-auto w-full h-full p-6 overflow-y-auto'>
                                            <div className="w-full  flex-col my-auto flex justify-center items-center gap-2">
                                                <img src={empty} className="w-[108px]" alt="empty" />
                                                <p className="mt-3 text-sm text-[#868C98] text-center w-1/3">
                                                    There are no messages available in this chat. <br />
                                                </p>
                                            </div>
                                        </div>
                                        :
                                        <motion.ul variants={messageVariants} className="flex flex-col gap-6">
                                            <>
                                                {renderMessages()}
                                            </>
                                            <div className="p-1" ref={messagesEndRef} />
                                        </motion.ul>
                            }
                        </div>
                        <form id='message' onSubmit={handleSubmit} className='w-full p-3'>
                            <section className='w-full flex items-end gap-3 p-2 rounded-xl border border-[#E2E4E9]'>
                                <textarea
                                    style={{
                                        outline: "none",
                                        border: "none",
                                        boxShadow: "none",
                                    }}
                                    type="text"
                                    value={text}
                                    onChange={(event) => setText(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' && !event.shiftKey) {
                                            handleSubmit(event);
                                        }
                                    }}
                                    placeholder="Type a message..."
                                    className="placeholder:text-sm focus:outline-none rounded-xl w-full text-xs text-tblack-100"
                                />

                                <button form='message' type='submit' className='text-sm bg-[#1A6A73] text-white py-3 px-4 rounded-full flex items-center gap-2'>
                                    Send
                                    <Icon.ChevronRight size={15} color='#FFF' />
                                </button>
                            </section>
                        </form>
                    </>
                    :
                    detailsLoading ?
                        <div className='flex items-center justify-center m-auto relative w-full h-full p-6 overflow-y-auto'>
                            <div className="w-full  flex-col my-auto flex justify-center items-center gap-2">
                                <Loader />
                            </div>
                        </div>
                        :
                        <div className='flex items-center justify-center m-auto relative w-full h-full p-6 overflow-y-auto'>
                            <div className="w-full  flex-col my-auto flex justify-center items-center gap-2">
                                <img src={empty} className="w-[108px]" alt="empty" />
                                <p className="mt-3 text-sm text-[#868C98] text-center w-1/3">
                                    Messages sent after connecting with a client will appear here. <br />
                                </p>
                                <button onClick={() => setShowNewChatSearch((prev) => !prev)} className='text-sm bg-[#1A6A73] text-white py-3 px-4 rounded-full flex items-center gap-2'>
                                    New Message
                                </button>
                            </div>
                        </div>
            }
        </div>
    )
}

export default ChatBox
